import { computed } from 'vue'
import { TinyColor } from '@ctrl/tinycolor'
import { useNamespace } from '@jdesign-vue/hooks'
import { useFormDisabled } from '@jdesign-vue/components/form'
import type { ButtonProps } from './button'

export function darken(color: TinyColor, amount = 20) {
  return color.mix('#141414', amount).toString()
}
function extractGradientColors(gradient: string) {
  const gradientRegex = /linear-gradient\(([^,]+),\s*([^)]+)\)/
  // 支持十六进制颜色和颜色名称的正则表达式
  const colorsRegex = /((?:#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3}|\w+))\s*([0-9.]+%)?/g
  const matches = gradient.match(gradientRegex)
  if (!matches) return []

  // 使用 exec 替代 matchAll，因为 TypeScript 配置不支持 ES2020
  const colors: string[] = []
  let match: RegExpExecArray | null
  while ((match = colorsRegex.exec(matches[2])) !== null) {
    colors.push(match[1])
  }
  return colors
}
function darkenGradient(color: string, factor = 0.2) {
  const tc = new TinyColor(color)
  const darkenedColor = tc.darken(factor * 100)
  return darkenedColor.toHexString()
}
function darkenGradients(gradientString: string, factor = 0.1) {
  let newGradientString = gradientString
  const colors = extractGradientColors(gradientString)
  colors.forEach(color => {
    newGradientString = newGradientString.replace(color, darkenGradient(color))
  })
  return newGradientString
}
export function useButtonCustomStyle(props: ButtonProps) {
  const _disabled = useFormDisabled()
  const ns = useNamespace('button')

  // calculate hover & active color by custom color
  // only work when custom color
  return computed(() => {
    let styles: Record<string, string> = {}

    const buttonColor = props.color
    const gradientColor = props.gradientColor

    if (buttonColor) {
      const color = new TinyColor(buttonColor)
      const activeBgColor = props.dark
        ? color.tint(20).toString()
        : darken(color, 20)

      if (props.plain) {
        styles = ns.cssVarBlock({
          'bg-color': props.dark
            ? darken(color, 90)
            : color.tint(90).toString(),
          'text-color': buttonColor,
          'border-color': props.dark
            ? darken(color, 50)
            : color.tint(50).toString(),
          'hover-text-color': `var(${ns.cssVarName('color-white')})`,
          'hover-bg-color': buttonColor,
          'hover-border-color': buttonColor,
          'active-bg-color': activeBgColor,
          'active-text-color': `var(${ns.cssVarName('color-white')})`,
          'active-border-color': activeBgColor,
        })

        if (_disabled.value) {
          styles[ns.cssVarBlockName('disabled-bg-color')] = props.dark
            ? darken(color, 90)
            : color.tint(90).toString()
          styles[ns.cssVarBlockName('disabled-text-color')] = props.dark
            ? darken(color, 50)
            : color.tint(50).toString()
          styles[ns.cssVarBlockName('disabled-border-color')] = props.dark
            ? darken(color, 80)
            : color.tint(80).toString()
        }
      } else {
        const hoverBgColor = props.dark
          ? darken(color, 30)
          : color.tint(30).toString()
        const textColor = color.isDark()
          ? `var(${ns.cssVarName('color-white')})`
          : `var(${ns.cssVarName('color-black')})`
        styles = ns.cssVarBlock({
          'bg-color': buttonColor,
          'text-color': textColor,
          'border-color': buttonColor,
          'hover-bg-color': hoverBgColor,
          'hover-text-color': textColor,
          'hover-border-color': hoverBgColor,
          'active-bg-color': activeBgColor,
          'active-border-color': activeBgColor,
        })

        if (_disabled.value) {
          const disabledButtonColor = props.dark
            ? darken(color, 50)
            : color.tint(50).toString()
          styles[ns.cssVarBlockName('disabled-bg-color')] = disabledButtonColor
          styles[ns.cssVarBlockName('disabled-text-color')] = props.dark
            ? 'rgba(255, 255, 255, 0.5)'
            : `var(${ns.cssVarName('color-white')})`
          styles[ns.cssVarBlockName('disabled-border-color')] =
            disabledButtonColor
        }
      }
    }

    // 处理gradientColor属性
    if (gradientColor) {
      // 当按钮是plain样式时应用渐变色到边框和文字
      if (props.plain || props.dashed || props.text) {
        styles[ns.cssVarBlockName('bg-color')] = 'transparent'
        styles[ns.cssVarBlockName('text-color')] = 'transparent'
        styles[ns.cssVarBlockName('border-color')] = gradientColor
        styles['background-image'] = gradientColor
        styles['background-clip'] = 'text'
        styles['-webkit-background-clip'] = 'text'
        styles[ns.cssVarBlockName('active-color')] = darkenGradients(gradientColor)
        // 为图标添加渐变色支持
        const icons = extractGradientColors(gradientColor)
        if (icons && icons.length > 0) {
          styles['--icon-color'] = icons[0]
          styles['--active-icon-color'] = darkenGradient(icons[0])
        }
        if (_disabled.value) {
          styles[ns.cssVarBlockName('disabled-text-color')] = 'transparent'
          styles[ns.cssVarBlockName('disabled-bg-color')] = 'transparent'
          styles[ns.cssVarBlockName('disabled-border-color')] = gradientColor
          styles['opacity'] = '0.6'
          styles['--icon-opacity'] = '0.6'
        }
      }
      // 当按钮不是plain和dashed时，背景色使用渐变色，文字和图标颜色为白色
      else {
        styles[ns.cssVarBlockName('bg-color')] = gradientColor
        styles[ns.cssVarBlockName('text-color')] = `var(${ns.cssVarName(
          'color-white'
        )})`
        styles[ns.cssVarBlockName('border-color')] = gradientColor
        styles[ns.cssVarBlockName('hover-bg-color')] = gradientColor
        styles[ns.cssVarBlockName('active-bg-color')] = darkenGradients(gradientColor)
        if (_disabled.value) {
          styles[ns.cssVarBlockName('disabled-bg-color')] = gradientColor
          styles[
            ns.cssVarBlockName('disabled-text-color')
          ] = `var(${ns.cssVarName('color-white')})`
          styles[ns.cssVarBlockName('disabled-border-color')] = gradientColor
          styles['opacity'] = '0.6'
        }
      }
    }

    return styles
  })
}
